import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { buildEvaluacionAmarre } from '../services/amarreService';
import api from '../services/api';
import {
  crearEvento,
  dispararWebhook,
} from '../services/eventoEvaluacionService';
import participanteToolboxService from '../services/participanteToolboxService';
import preguntaService from '../services/preguntaService';
import respuestaService from '../services/respuestaService';
import sesionToolboxService from '../services/sesionToolboxService';
import { buildEvaluacionTrianual } from '../services/evaluacionesTrianualesService';
import { useAuthStore } from './authStore';

function formatDate(date) {
  return new Intl.DateTimeFormat('es-AR').format(date);
}

function normalizeCompletedEvaluation(evaluacionActual, respuestas) {
  const now = new Date();
  const session = useAuthStore.getState().session;
  const responsable = session?.user;

  return {
    ...evaluacionActual,
    fecha: evaluacionActual?.fechaHora
      ? formatDate(new Date(evaluacionActual.fechaHora))
      : formatDate(now),
    fechaHora: evaluacionActual?.fechaHora ?? now.toISOString(),
    tipoReporte: evaluacionActual?.tipo || 'trianual',
    etiquetaReporte: (evaluacionActual?.tipo || 'trianual').toUpperCase(),
    nombreResponsable: responsable
      ? `${responsable.apellido}, ${responsable.nombre}`.toUpperCase()
      : evaluacionActual?.nombreResponsable || '',
    respuestas,
  };
}

function upsertRespuesta(respuestas, nextRespuesta) {
  const existingIndex = respuestas.findIndex(
    (item) => item.preguntaId === nextRespuesta.preguntaId
  );

  if (existingIndex === -1) {
    return [...respuestas, nextRespuesta];
  }

  return respuestas.map((item, index) =>
    index === existingIndex ? nextRespuesta : item
  );
}

function buildRespuestaValue(respuesta) {
  if (respuesta === 'si') {
    return 'cumple';
  }

  if (respuesta === 'no') {
    return 'no_cumple';
  }

  return 'no_aplica';
}

async function buildEvaluacionActual(
  persistedEvaluacion,
  evaluado,
  options = {}
) {
  const operariosEvaluados =
    options.operariosEvaluados || persistedEvaluacion.operariosEvaluados || [];

  if (persistedEvaluacion.tipo === 'trianual') {
    return {
      ...persistedEvaluacion,
      ...buildEvaluacionTrianual(
        evaluado || {
          id: persistedEvaluacion.evaluadoId,
          nombre: '',
          legajo: '',
          examen: 'TRIANUAL',
        }
      ),
      id: persistedEvaluacion.id,
      tipo: persistedEvaluacion.tipo,
      responsableId: persistedEvaluacion.responsableId,
      evaluadoId: persistedEvaluacion.evaluadoId,
      fechaHora: persistedEvaluacion.fechaHora,
    };
  }

  if (persistedEvaluacion.tipo === 'amarre') {
    const preguntas = await preguntaService.getByTipo('amarre');

    return {
      ...persistedEvaluacion,
      operariosEvaluados,
      ...buildEvaluacionAmarre(
        evaluado || {
          id: persistedEvaluacion.evaluadoId,
          nombre: '',
          apellido: '',
          legajo: '',
        },
        preguntas
      ),
      id: persistedEvaluacion.id,
      tipo: persistedEvaluacion.tipo,
      responsableId: persistedEvaluacion.responsableId,
      evaluadoId: persistedEvaluacion.evaluadoId,
      fechaHora: persistedEvaluacion.fechaHora,
    };
  }

  return {
    ...persistedEvaluacion,
    nombreEvaluado: evaluado?.nombre || '',
    legajoEvaluado: evaluado?.legajo || '',
    examen: persistedEvaluacion.tipo.toUpperCase(),
    firma: '',
    preguntas: [],
  };
}

export const useEvaluationStore = create(
  persist(
    (set, get) => ({
      evaluacionActual: null,
      evaluacionesCompletadas: [],
      respuestas: [],
      creandoEvaluacion: false,
      creandoInforme: false,
      guardandoRespuesta: false,
      creandoSesion: false,
      errorEvaluacion: null,
      errorInforme: null,
      errorRespuesta: null,
      errorSesion: null,
      successEvaluacion: null,
      successInforme: null,
      successRespuesta: null,
      sesionActual: null,
      async iniciarEvaluacion(
        input,
        maybeResponsableId,
        maybeEvaluadoId,
        extraData = {}
      ) {
        let tipo = 'trianual';
        let responsableId = maybeResponsableId;
        let evaluadoId = maybeEvaluadoId;
        let evaluado = null;

        if (
          typeof input === 'object' &&
          input !== null &&
          maybeResponsableId === undefined &&
          maybeEvaluadoId === undefined
        ) {
          evaluado = input;
          const session = useAuthStore.getState().session;

          if (!session?.user?.id) {
            const error = new Error('No hay un responsable logueado.');
            set({
              creandoEvaluacion: false,
              creandoInforme: false,
              errorEvaluacion: error.message,
              errorInforme: error.message,
              successEvaluacion: null,
              successInforme: null,
            });
            throw error;
          }

          responsableId = session.user.id;
          evaluadoId = input.id;
        } else {
          tipo = input || 'trianual';

          if (typeof maybeEvaluadoId === 'object' && maybeEvaluadoId !== null) {
            evaluado = maybeEvaluadoId;
            evaluadoId = maybeEvaluadoId.id;
          }
        }

        if (typeof input === 'object' && input !== null && input.examen) {
          tipo = input.examen.toLowerCase();
        }

        set({
          evaluacionActual: null,
          respuestas: [],
          creandoEvaluacion: true,
          creandoInforme: true,
          errorEvaluacion: null,
          errorInforme: null,
          successEvaluacion: null,
          successInforme: null,
          errorRespuesta: null,
          successRespuesta: null,
        });

        try {
          const { operariosEvaluados = [] } = extraData;

          if (tipo === 'trianual') {
            // Verificar si ya existe un informe en borrador para este evaluado
            const informeExistenteResponse = await api.get(
              `/api/informes/?evaluado=${evaluado?.id_entidad || evaluadoId}&estado=2`
            );
            const informesExistentes = informeExistenteResponse.data || [];
            if (informesExistentes.length > 0) {
              // Recuperar el informe más reciente
              const informeExistente = informesExistentes[0];
              const id_informe = informeExistente.id;
              const id_evaluacion = informeExistente.id_evaluacion;
              const id_evento = informeExistente.id_evento;

              // Obtener items con respuestas del informe existente
              const itemsResponse = await api.get(
                `/api/informes/items?evaluacion=${id_evaluacion}`
              );
              const itemsEvaluacion = itemsResponse.data || [];
              const itemEvaluacion = itemsEvaluacion.find(
                (i) => i.id_evaluacion === id_evaluacion
              );
              const id_item_evaluacion = itemEvaluacion?.id;
              const id_evidencia = itemEvaluacion?.Item_Evaluacion?.[0]?.Evidencias?.[0]?.id ?? null;
              if (!id_item_evaluacion) throw new Error('No se encontró el item de evaluación');

              // Reconstruir preguntas con respuestas guardadas
              const preguntas = (itemEvaluacion.Item_Evaluacion?.[0]?.Preguntas || [])
                .filter(p => typeof p === 'object')
                .map((p, idx) => ({
                  id: String(p.id),
                  preguntaId: p.id,
                  id_item_evaluacion,
                  id_evidencia,
                  pregunta: p.titulo,
                  respuesta: (() => {
                    const r = p.Respuesta?.[0]?.respuesta;
                    if (r === 'cumple') return 'si';
                    if (r === 'no_cumple') return 'no';
                    if (r === 'no_aplica') return 'no aplica';
                    return '';
                  })(),
                  observacion: '',
                  requiere_evidencia: false,
                  evidencia: '',
                  evidenciaPreview: '',
                  orden: p.orden || idx + 1
                }));
              if (!preguntas.length) throw new Error('No se encontraron preguntas');

              const evaluacionActual = {
                id: id_evaluacion,
                id_informe,
                id_evento,
                tipo: 'trianual',
                responsableId,
                evaluadoId: evaluado?.id_entidad || evaluadoId,
                nombreEvaluado: evaluado?.nombre || '',
                legajoEvaluado: evaluado?.legajo || '',
                examen: 'TRIANUAL',
                firma: '',
                preguntas
              };

              set({
                evaluacionActual,
                creandoEvaluacion: false,
                creandoInforme: false,
                errorEvaluacion: null,
                errorInforme: null,
                successEvaluacion: 'Evaluacion trianual recuperada correctamente.',
                successInforme: 'Informe trianual recuperado correctamente.'
              });

              (async () => {
                try {
                  const evidenciasGuardadas = await api.fetchEvidenciasInforme(id_item_evaluacion);
                  if (!evidenciasGuardadas.length) return;

                  const descargadas = await Promise.all(
                    evidenciasGuardadas.map(async (ev) => ({
                      id_pregunta_item: ev.id_pregunta_item,
                      referencia_archivo: ev.referencia_archivo,
                      dataUrl: await api.fetchEvidenciaComoDataUrl(ev.referencia_archivo),
                    }))
                  );

                  const previewPorPregunta = {};
                  const referenciaPorPregunta = {};
                  for (const { id_pregunta_item, referencia_archivo, dataUrl } of descargadas) {
                    if (!dataUrl) continue;
                    previewPorPregunta[String(id_pregunta_item)] = dataUrl;
                    referenciaPorPregunta[String(id_pregunta_item)] = referencia_archivo;
                  }

                  if (!Object.keys(previewPorPregunta).length) return;

                  const current = get().evaluacionActual;
                  if (!current || current.id !== id_evaluacion) return;

                  set({
                    evaluacionActual: {
                      ...current,
                      preguntas: current.preguntas.map((p) => {
                        const key = String(p.preguntaId ?? p.id);
                        const dataUrl = previewPorPregunta[key];
                        if (!dataUrl) return p;
                        return {
                          ...p,
                          evidenciaPreview: dataUrl,
                          evidencia: referenciaPorPregunta[key] || p.evidencia,
                        };
                      }),
                    },
                  });
                } catch {
                  // no bloquear ni alterar la apertura del informe
                }
              })();

              return evaluacionActual.preguntas[0]?.id;
            }

            // Paso 1: crear evento
            const eventoResponse = await crearEvento({
              nombre: `Evaluación Trianual - ${evaluado?.nombre || evaluadoId}`,
              descripcion: 'Generado desde asignación WMS',
              fecha_programada:
                evaluado?.fecha || new Date().toISOString().split('T')[0],
              origen: 'wms',
              id_plantilla: 3,
              evento_metadata: JSON.stringify({
                id_asignacion_wms: evaluado?.id_asignacion_wms || null,
              }),
            });
            const id_evento = eventoResponse.data?.id;
            if (!id_evento) throw new Error('No se pudo crear el evento');

            // Paso 2: disparar webhook
            const webhookResponse = await dispararWebhook({
              id_evento,
              id_evaluado: evaluado?.id_entidad || evaluadoId,
            });
            const { id_informe, id_evaluacion } = webhookResponse.data;
            if (!id_informe) throw new Error('No se pudo crear el informe');

            // Paso 3: obtener preguntas reales de la plantilla
            const plantillaResponse = await api.get('/api/evaluaciones/plantillas/3');
            const items = plantillaResponse.data?.[0]?.Items || [];
            const preguntasItems = items.flatMap((item) =>
              Array.isArray(item.Preguntas) ? item.Preguntas : []
            );

            if (!preguntasItems.length) throw new Error('No se encontraron preguntas');

            // Obtener id_item_evaluacion del snapshot creado por el webhook
            const itemsResponse = await api.get(
              `/api/informes/items?evaluacion=${id_evaluacion}`
            );
            const itemsEvaluacion = itemsResponse.data || [];
            const itemEvaluacion = itemsEvaluacion.find(
              (i) => i.id_evaluacion === id_evaluacion
            );
            const id_item_evaluacion = itemEvaluacion?.id;
            const id_evidencia = itemEvaluacion?.Item_Evaluacion?.[0]?.Evidencias?.[0]?.id ?? null;
            if (!id_item_evaluacion) throw new Error('No se encontró el item de evaluación');

            const preguntas = preguntasItems.map((p, idx) => ({
              id: String(p.id),
              preguntaId: p.id,
              id_item_evaluacion,
              id_evidencia,
              pregunta: p.titulo,
              respuesta: '',
              observacion: '',
              requiere_evidencia: false,
              evidencia: '',
              evidenciaPreview: '',
              orden: p.orden || idx + 1,
            }));

            const evaluacionActual = {
              id: id_evaluacion,
              id_informe,
              id_evento,
              tipo: 'trianual',
              responsableId,
              evaluadoId: evaluado?.id_entidad || evaluadoId,
              nombreEvaluado: evaluado?.nombre || '',
              legajoEvaluado: evaluado?.legajo || '',
              examen: 'TRIANUAL',
              firma: '',
              preguntas,
            };

            set({
              evaluacionActual,
              creandoEvaluacion: false,
              creandoInforme: false,
              errorEvaluacion: null,
              errorInforme: null,
              successEvaluacion: 'Evaluacion trianual creada correctamente.',
              successInforme: 'Informe trianual creado correctamente.',
            });

            return evaluacionActual.preguntas[0]?.id;
          }

          if (tipo === 'amarre') {
            // Los operarios vienen de AmarrePage — cada uno genera un informe separado
            // Tomamos el primer operario seleccionado como evaluado principal
            const operarioPrincipal = operariosEvaluados[0] || evaluado || { id: evaluadoId, nombre: '', legajo: '' };

            // Paso 1: crear evento
            const eventoResponse = await crearEvento({
              nombre: `Evaluación Amarre - ${operarioPrincipal.nombre || operarioPrincipal.legajo || evaluadoId}`,
              descripcion: 'Generado desde módulo de amarre',
              fecha_programada: extraData?.amarreContext?.fecha || new Date().toISOString().split('T')[0],
              origen: 'amarre',
              id_plantilla: 4,
              metadata: {
                amarreContext: extraData?.amarreContext || {},
              },
            });
            const id_evento = eventoResponse.data?.id;
            if (!id_evento) throw new Error('No se pudo crear el evento de amarre');

            // Paso 2: crear un informe por cada operario evaluado
            const informesPorOperario = await Promise.all(
              operariosEvaluados.map(async (op) => {
                const webhookResp = await dispararWebhook({
                  id_evento,
                  id_evaluado: Number(op.id_entidad) || Number(op.id) || null,
                });
                const { id_informe, id_evaluacion } = webhookResp.data;
                if (!id_informe) throw new Error(`No se pudo crear informe para ${op.nombre}`);

                // Obtener id_item_evaluacion del snapshot de este operario
                const itemsResp = await api.get(`/api/informes/items?evaluacion=${id_evaluacion}`);
                const itemsEvaluacion = itemsResp.data || [];
                const itemEvaluacion = itemsEvaluacion.find(i => i.id_evaluacion === id_evaluacion);
                const id_item_evaluacion = itemEvaluacion?.id;
                const id_evidencia = itemEvaluacion?.Item_Evaluacion?.[0]?.Evidencias?.[0]?.id ?? null;
                if (!id_item_evaluacion) throw new Error(`No se encontró item para ${op.nombre}`);

                return {
                  id_informe,
                  id_evaluacion,
                  id_item_evaluacion,
                  id_evidencia,
                  id_evaluado: Number(op.id_entidad) || Number(op.id),
                  nombre: op.nombre,
                  legajo: op.legajo,
                };
              })
            );

            // Paso 3: obtener preguntas de la plantilla amarre (id=4)
            const plantillaResponse = await api.get('/api/evaluaciones/plantillas/4');
            const items = plantillaResponse.data?.[0]?.Items || [];
            const preguntasItems = items.flatMap((item) =>
              Array.isArray(item.Preguntas) ? item.Preguntas : []
            );
            if (!preguntasItems.length) throw new Error('No se encontraron preguntas de amarre');

            // Usar el primer informe como referencia para las preguntas
            const primerInforme = informesPorOperario[0];
            const id_item_evaluacion = primerInforme?.id_item_evaluacion;
            const id_evidencia = primerInforme?.id_evidencia ?? null;
            if (!id_item_evaluacion) throw new Error('No se encontró el item de evaluación de amarre');

            const preguntas = preguntasItems.map((p, idx) => ({
              id: String(p.id),
              preguntaId: p.id,
              id_item_evaluacion: primerInforme.id_item_evaluacion,
              id_evidencia,
              pregunta: p.titulo,
              respuesta: '',
              observacion: '',
              requiere_evidencia: false,
              evidencia: '',
              evidenciaPreview: '',
              orden: p.orden || idx + 1,
            }));

            const evaluacionActual = {
              id: primerInforme.id_evaluacion,
              id_informe: primerInforme.id_informe,
              id_evento,
              tipo: 'amarre',
              responsableId,
              evaluadoId: primerInforme.id_evaluado,
              nombreEvaluado: primerInforme.nombre || '',
              legajoEvaluado: primerInforme.legajo || '',
              examen: 'AMARRE',
              firma: '',
              operariosEvaluados,
              amarreContext: extraData?.amarreContext || {},
              // Array con todos los informes para guardar respuestas en todos
              informesAmarre: informesPorOperario,
              preguntas,
            };

            set({
              evaluacionActual,
              creandoEvaluacion: false,
              creandoInforme: false,
              errorEvaluacion: null,
              errorInforme: null,
              successEvaluacion: 'Evaluacion amarre creada correctamente.',
              successInforme: 'Informe amarre creado correctamente.',
            });

            return evaluacionActual.preguntas[0]?.id;
          }
        } catch (error) {
          set({
            creandoEvaluacion: false,
            creandoInforme: false,
            errorEvaluacion: error.message || 'No se pudo crear la evaluacion.',
            errorInforme: error.message || 'No se pudo crear la evaluacion.',
            successEvaluacion: null,
            successInforme: null,
          });
          throw error;
        }
      },
      async iniciarSesion(data, participantesIds = []) {
        set({
          sesionActual: null,
          creandoSesion: true,
          errorSesion: null,
        });

        try {
          const sesionActual = await sesionToolboxService.create(data);
          const participantesPayload = participantesIds.map((operarioId) => ({
            sesionId: sesionActual.id,
            operarioId,
          }));

          if (participantesPayload.length) {
            await participanteToolboxService.bulkCreate(participantesPayload);
          }

          set({
            sesionActual,
            creandoSesion: false,
            errorSesion: null,
          });

          return sesionActual;
        } catch (error) {
          set({
            creandoSesion: false,
            errorSesion: error.message || 'No se pudo crear la sesion.',
          });
          throw error;
        }
      },
      async guardarRespuesta(questionId, respuesta, observacion = '') {
        const evaluacionActual = get().evaluacionActual;

        if (!evaluacionActual?.id) {
          return null;
        }

        const currentQuestion = evaluacionActual.preguntas.find(
          (item) => String(item.id) === String(questionId)
        );

        if (!currentQuestion) {
          return null;
        }

        set({
          evaluacionActual: {
            ...evaluacionActual,
            preguntas: evaluacionActual.preguntas.map((item) =>
              String(item.id) === String(questionId)
                ? { ...item, respuesta, observacion }
                : item
            ),
          },
          guardandoRespuesta: true,
          errorRespuesta: null,
          successRespuesta: null,
        });

        try {
          const id_item_evaluacion = currentQuestion.id_item_evaluacion;
          const id_pregunta_item = Number(currentQuestion.preguntaId || currentQuestion.id);
          const respuestaValor = buildRespuestaValue(respuesta);

          const persisted = await respuestaService.guardarRespuestaPregunta(
            id_item_evaluacion,
            id_pregunta_item,
            respuestaValor
          );

          // Si es amarre con múltiples informes, guardar en todos los demás
          if (evaluacionActual.informesAmarre && evaluacionActual.informesAmarre.length > 1) {
            await Promise.all(
              evaluacionActual.informesAmarre.slice(1).map(informe =>
                respuestaService.guardarRespuestaPregunta(
                  informe.id_item_evaluacion,
                  id_pregunta_item,
                  respuestaValor
                ).catch(err => console.error(`[AMARRE] Error guardando respuesta en informe ${informe.id_informe}:`, err.message))
              )
            );
          }

          set((state) => ({
            respuestas: upsertRespuesta(state.respuestas, {
              preguntaId: id_pregunta_item,
              respuesta: respuestaValor,
              observacion,
            }),
            guardandoRespuesta: false,
            errorRespuesta: null,
            successRespuesta: {
              questionId: String(questionId),
              message: 'Respuesta guardada correctamente.',
              timestamp: Date.now(),
            },
          }));

          return persisted;
        } catch (error) {
          set({
            guardandoRespuesta: false,
            errorRespuesta: error.message || 'No se pudo guardar la respuesta.',
            successRespuesta: null,
          });
          throw error;
        }
      },
      responderPregunta(questionId, respuesta, observacion = '') {
        return get().guardarRespuesta(questionId, respuesta, observacion);
      },
      limpiarErrorRespuesta() {
        set({ errorRespuesta: null });
      },
      guardarEvidencia(questionId, evidenciaPayload) {
        const evaluacionActual = get().evaluacionActual;

        if (!evaluacionActual) {
          return;
        }

        const nextEvidence =
          typeof evidenciaPayload === 'string'
            ? {
              evidencia: evidenciaPayload,
              evidenciaPreview: '',
            }
            : {
              evidencia: evidenciaPayload?.name || '',
              evidenciaPreview: evidenciaPayload?.dataUrl || '',
            };

        set({
          evaluacionActual: {
            ...evaluacionActual,
            preguntas: evaluacionActual.preguntas.map((item) =>
              String(item.id) === String(questionId)
                ? {
                  ...item,
                  evidencia: nextEvidence.evidencia,
                  evidenciaPreview: nextEvidence.evidenciaPreview,
                }
                : item
            ),
          },
        });
      },
      guardarFirma(firma) {
        const evaluacionActual = get().evaluacionActual;

        if (!evaluacionActual) {
          return;
        }

        set({
          evaluacionActual: {
            ...evaluacionActual,
            firma,
          },
        });
      },
      guardarObservacion(questionId, observacion) {
        const evaluacionActual = get().evaluacionActual;

        if (!evaluacionActual) {
          return;
        }

        set({
          evaluacionActual: {
            ...evaluacionActual,
            preguntas: evaluacionActual.preguntas.map((item) =>
              String(item.id) === String(questionId)
                ? {
                  ...item,
                  observacion,
                }
                : item
            ),
          },
        });
      },
      guardarEvaluacionCompletada(evaluacion) {
        set((state) => ({
          evaluacionesCompletadas: [...state.evaluacionesCompletadas, evaluacion],
        }));
      },
      resetearEvaluacion() {
        const evaluacionActual = get().evaluacionActual;
        const respuestas = get().respuestas;

        if (evaluacionActual) {
          get().guardarEvaluacionCompletada(
            normalizeCompletedEvaluation(evaluacionActual, respuestas)
          );
        }

        set({
          evaluacionActual: null,
          respuestas: [],
          creandoEvaluacion: false,
          creandoInforme: false,
          guardandoRespuesta: false,
          errorEvaluacion: null,
          errorInforme: null,
          errorRespuesta: null,
          successEvaluacion: null,
          successInforme: null,
          successRespuesta: null,
        });
      },
    }),
    {
      name: 'evaluation-store',
    }
  )
);
